import type { RuntimeConfig } from "./runtime-config.js";

/**
 * The boot decision `FE-06`/`FE-07` require: nothing renders until the runtime configuration has
 * loaded and validated, and a document that is missing or malformed produces a visible error
 * instead of a mounted application.
 *
 * Extracted out of `main.tsx` for the reason `docs/TESTING-STANDARDS.md` states under "Seams", and
 * for a specific reason beyond tidiness: while this branch lived in the entry module it was the one
 * part of the feature no test could reach. The loader had its own specs and the error renderer had
 * its own specs, but deleting the gate between them - mounting unconditionally, or mounting before
 * the load settled - broke no test at all, so the acceptance criterion that "a missing or malformed
 * runtime configuration never mounts" was asserted by nobody. `bootstrap.spec.ts` asserts it here.
 *
 * `mount` runs outside the `catch`, deliberately. Inside it, a React render failure would be
 * reported to the operator as a configuration error, sending them to inspect a document that is
 * perfectly valid. A render failure is an application defect, so it rejects and surfaces as the
 * uncaught error it is.
 */
export interface BootstrapDependencies {
  readonly loadConfig: () => Promise<RuntimeConfig>;
  readonly mount: (config: RuntimeConfig) => void;
  readonly renderError: (message: string) => void;
}

export async function bootstrapApplication({
  loadConfig,
  mount,
  renderError,
}: BootstrapDependencies): Promise<void> {
  let config: RuntimeConfig;
  try {
    config = await loadConfig();
  } catch (error) {
    renderError(error instanceof Error ? error.message : String(error));
    return;
  }

  mount(config);
}
